import { useTranslation } from "react-i18next"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { InputPhotoWithContainer } from "../../../components/InputPhoto/InputPhoto"
import GetSpace from "../../../utils/space"
import {
  faClose,
  faHouse,
  faInfoCircle,
  faMessage,
  faSquareBinary,
  faTrademark,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import InputField from "../../../components/InputField/InputField"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import { useEffect, useMemo } from "react"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { useRegister } from "../../../hooks/Register/useRegister"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useFileImage } from "../../../hooks/useFileImage"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { CanView } from "../../../components/CanView/CanView"
import { getWorldwideTimeZoneOptions, resolveStoreTimeZone } from "../../../utils/timeZone"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { CNPJ_FORMATTED_LENGTH, formatCnpj } from "../../../utils/documentFormat"

interface CompanyDataPupopProps {
  onClonse: () => void
}

export const CompanyDataPupop: React.FC<CompanyDataPupopProps> = ({ onClonse }) => {
  const { t } = useTranslation("company")

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { fileImage, setFileImage } = useFileImage()

  const { handleGetCompany } = useGetCompany()

  const { handleSaveCompany } = useSaveCompany()

  const {
    nameFantasy,
    nameSocialReason,
    cnpj,
    stateSubscribe,
    nameMessage,
    handleNameMessageChange,
    handleStateSubscribeChange,
    handleCnpjChange,
    handleNameSocialReasonChange,
    handleNameFantasyChange,
    timeZoneId,
    handleTimeZoneIdChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreateCompany()

  const storeTimeZones = useMemo(() => {
    const baseOptions = getWorldwideTimeZoneOptions()

    const selected = resolveStoreTimeZone(timeZoneId)

    if (baseOptions.some((item) => item.value === selected)) {
      return baseOptions
    }

    return [{ value: selected, label: selected, offsetMinutes: 0 }, ...baseOptions]
  }, [timeZoneId])

  useEffect(() => {
    handleInitialValue(companyMemory)
  }, [handleInitialValue])

  const handleTrySave = () => {
    const data = handleSubmitForm()

    handleSaveCompany(
      data,
      () => {
        handleGetCompany(() => handleInitialValue(companyMemory))

        onClonse()
      },
      fileImage,
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_company_data")}
              iconColor={THEME.colors.iconBGColor}
              icon={faHouse}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.mystore.mycompany.companydatapupop.1"
                    variant="global"
                    title={t("helpPopup:global.storeData.title")}
                    description={t("helpPopup:global.storeData.description")}
                    bullets={t("helpPopup:global.storeData.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClonse}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.button.onClonse"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                handleTrySave()
              }}
            >
              <InputPhotoWithContainer
                iconButton={true}
                handleFileSelected={setFileImage}
                disable={canEdite}
                initialPhoto={companyMemory.iconUrl}
                imageProfile="company"
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className="flex flex-col gap-2">
                <GenericDropDown
                  legend={t("label_store_time_zone")}
                  disable={canEdite}
                  initialState={timeZoneId}
                  data={storeTimeZones.map((tz) => ({ label: tz.label, value: tz.value }))}
                  handlerFuncion={handleTimeZoneIdChange}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.select.fusoHorarioDaLoja"
                />
              </div>

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.name}
                type="text"
                placeholder={t("label_fantasy_name_place_holder")}
                onInvalidMessage={t("label_fantasy_name_onInvalidMessage")}
                icon={faInfoCircle}
                disable={canEdite}
                value={nameFantasy}
                onChange={handleNameFantasyChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.input.fantasyNamePlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.socialReason}
                type="text"
                placeholder={t("label_real_name_place_holder")}
                onInvalidMessage={t("label_real_name_onInvalidMessage")}
                icon={faTrademark}
                disable={canEdite}
                value={nameSocialReason}
                onChange={handleNameSocialReasonChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.input.realNamePlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.publicMessage}
                type="text"
                placeholder={t("label_public_message_place_holder")}
                onInvalidMessage={t("label_public_message_onInvalidMessage")}
                icon={faMessage}
                disable={canEdite}
                value={nameMessage}
                onChange={handleNameMessageChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.input.publicMessagePlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={CNPJ_FORMATTED_LENGTH}
                type="text"
                inputMode="numeric"
                placeholder={t("label_cnpj_place_holder")}
                onInvalidMessage={t("label_cnpj_onInvalidMessage")}
                icon={faWarehouse}
                disable={canEdite}
                value={formatCnpj(cnpj)}
                onChange={handleCnpjChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.input.cnpjPlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.stateInscrition}
                type="text"
                placeholder={t("label_state_registration_place_holder")}
                onInvalidMessage={t("label_state_registration_onInvalidMessage")}
                icon={faSquareBinary}
                disable={canEdite}
                value={stateSubscribe}
                onChange={handleStateSubscribeChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.input.stateRegistrationPlaceHolder"
              />

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    sizeNumber={SIZE.buttonCloseIconSize}
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.button.save"
                  />

                  <GetSpace right={5} />
                </CanView>

                <Button
                  label={t("buttons:button_register")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGRegisterColor}
                  onClick={handleEnableRegister}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyDataPupop.button.register"
                />
              </section>
            </form>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
